/**
 * productFoamBomApiClient.ts
 *
 * Frontend API client communicating with PostgreSQL backend for Product Foam / Packaging BOM.
 */
import type {
  FoamBomConfig,
  FoamBomLine,
  FoamBomRevision,
} from '@/features/boms/types/foamBomTypes'
import { formatDate } from '@/lib/dateFormat'

export async function getProductFoamBomConfigsApi(
  productId: string,
): Promise<FoamBomConfig[]> {
  const res = await fetch(`/api/products/${encodeURIComponent(productId)}/foam-bom/configs`)
  if (!res.ok) {
    throw new Error(`Lỗi tải danh sách cấu hình BOM Đóng Gói (${res.status})`)
  }
  const json = await res.json()
  return (json.configs || []).map((c: any) => ({
    id: c.id,
    product_id: c.product_id,
    assembly_type: c.assembly_type || 'kd',
    packaging_type: c.packaging_type || 'thuong',
    status: c.status || 'Đang Cập Nhật',
    sort_order: typeof c.sort_order === 'number' ? c.sort_order : 0,
    phu_bi_dai: c.phu_bi_dai != null ? Number(c.phu_bi_dai) : null,
    phu_bi_rong: c.phu_bi_rong != null ? Number(c.phu_bi_rong) : null,
    phu_bi_cao: c.phu_bi_cao != null ? Number(c.phu_bi_cao) : null,
    net_weight: c.net_weight != null ? Number(c.net_weight) : null,
    gross_weight: c.gross_weight != null ? Number(c.gross_weight) : null,
    created_at: c.created_at,
    updated_at: c.updated_at,
  }))
}

export async function saveProductFoamBomConfigsApi(
  productId: string,
  configs: FoamBomConfig[],
): Promise<FoamBomConfig[]> {
  const res = await fetch(`/api/products/${encodeURIComponent(productId)}/foam-bom/configs`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ configs }),
  })
  if (!res.ok) {
    throw new Error(`Lỗi lưu danh sách cấu hình BOM Đóng Gói (${res.status})`)
  }
  const json = await res.json()
  return (json.configs || []).map((c: any) => ({
    id: c.id,
    product_id: c.product_id,
    assembly_type: c.assembly_type,
    packaging_type: c.packaging_type,
    status: c.status || 'Đang Cập Nhật',
    sort_order: c.sort_order,
    phu_bi_dai: c.phu_bi_dai != null ? Number(c.phu_bi_dai) : null,
    phu_bi_rong: c.phu_bi_rong != null ? Number(c.phu_bi_rong) : null,
    phu_bi_cao: c.phu_bi_cao != null ? Number(c.phu_bi_cao) : null,
    net_weight: c.net_weight != null ? Number(c.net_weight) : null,
    gross_weight: c.gross_weight != null ? Number(c.gross_weight) : null,
    created_at: c.created_at,
    updated_at: c.updated_at,
  }))
}

export async function updateProductFoamBomConfigApi(
  productId: string,
  configId: string,
  patch: Partial<FoamBomConfig>,
): Promise<FoamBomConfig> {
  const res = await fetch(
    `/api/products/${encodeURIComponent(productId)}/foam-bom/configs/${encodeURIComponent(configId)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    },
  )
  if (!res.ok) {
    throw new Error(`Lỗi cập nhật cấu hình BOM Đóng Gói (${res.status})`)
  }
  const json = await res.json()
  const c = json.config
  return {
    id: c.id,
    product_id: c.product_id,
    assembly_type: c.assembly_type,
    packaging_type: c.packaging_type,
    status: c.status,
    sort_order: c.sort_order,
    phu_bi_dai: c.phu_bi_dai != null ? Number(c.phu_bi_dai) : null,
    phu_bi_rong: c.phu_bi_rong != null ? Number(c.phu_bi_rong) : null,
    phu_bi_cao: c.phu_bi_cao != null ? Number(c.phu_bi_cao) : null,
    net_weight: c.net_weight != null ? Number(c.net_weight) : null,
    gross_weight: c.gross_weight != null ? Number(c.gross_weight) : null,
    created_at: c.created_at,
    updated_at: c.updated_at,
  }
}

export async function getProductFoamBomDataApi(
  productId: string,
  configId: string,
): Promise<{ lines: FoamBomLine[] }> {
  const res = await fetch(
    `/api/products/${encodeURIComponent(productId)}/foam-bom/data/${encodeURIComponent(configId)}`,
  )
  if (!res.ok) {
    throw new Error(`Lỗi tải dữ liệu BOM Đóng Gói (${res.status})`)
  }
  const json = await res.json()
  return {
    lines: Array.isArray(json.data?.lines) ? json.data.lines : [],
  }
}

export async function saveProductFoamBomDataApi(
  productId: string,
  configId: string,
  lines: FoamBomLine[],
): Promise<void> {
  const res = await fetch(
    `/api/products/${encodeURIComponent(productId)}/foam-bom/data/${encodeURIComponent(configId)}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lines }),
    },
  )
  if (!res.ok) {
    throw new Error(`Lỗi lưu dữ liệu BOM Đóng Gói (${res.status})`)
  }
}

export async function getProductFoamBomRevisionsApi(
  productId: string,
  configId: string,
): Promise<FoamBomRevision[]> {
  const res = await fetch(
    `/api/products/${encodeURIComponent(productId)}/foam-bom/revisions/${encodeURIComponent(configId)}`,
  )
  if (!res.ok) {
    throw new Error(`Lỗi tải lịch sử phiên bản BOM Đóng Gói (${res.status})`)
  }
  const json = await res.json()
  return (json.revisions || []).map((r: any) => ({
    id: r.id,
    revNumber: r.rev_number,
    revLabel: r.rev_label,
    author: r.author,
    changesSummary: r.changes_summary,
    diffs: Array.isArray(r.diffs) ? r.diffs : [],
    snapshot: r.snapshot || { lines: [] },
    savedAt: r.created_at ? formatDate(r.created_at) : '',
    savedTime: r.created_at
      ? new Date(r.created_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
      : '',
  }))
}

export async function createProductFoamBomRevisionApi(
  productId: string,
  configId: string,
  revision: FoamBomRevision,
): Promise<void> {
  const res = await fetch(
    `/api/products/${encodeURIComponent(productId)}/foam-bom/revisions/${encodeURIComponent(configId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: revision.id,
        revNumber: revision.revNumber,
        revLabel: revision.revLabel,
        author: revision.author,
        changesSummary: revision.changesSummary,
        diffs: revision.diffs,
        snapshot: revision.snapshot,
      }),
    },
  )
  if (!res.ok) {
    throw new Error(`Lỗi lưu phiên bản BOM Đóng Gói (${res.status})`)
  }
}
